import JSZip from "jszip";
import toast from "react-hot-toast";
import { createActionError } from "@/client-error";
import { actions } from "@/controller";
import { i18n } from "@/locales";
import type { Transcript } from "@/types";
import { formatDuration } from "@/core";
import { downloadFile } from "@/file-download";
import { getPlatform, isNativeApp } from "@/platform";
import { SUPPORTED_FILE_EXTENSIONS } from "@/file-types";
import {
  getTranscriptDirection,
  getTextDirection,
  type TextDirection,
} from "@/text-direction";

const downloadTranscriptAsDocx = async ({
  fileName,
  items,
  direction,
  returnBlobOrBase64 = false,
}: {
  fileName?: string;
  items: { text: string; metadata?: string }[];
  direction: TextDirection;
  returnBlobOrBase64?: boolean;
}): Promise<Blob | string | undefined> => {
    const { AlignmentType, Document, Paragraph, TextRun, Packer } =
      await import("docx");
    const rtl = direction === "rtl";

    const childrenArray: any[] = [];

    items.forEach((item) => {
      const paragraph = new Paragraph({
        children: [],
        alignment: AlignmentType.START,
        bidirectional: rtl,
      });

      if (item.metadata) {
        const metadataRun = new TextRun({
          text: item.metadata,
          rightToLeft: rtl,
        });
        paragraph.addChildElement(metadataRun);
        paragraph.addChildElement(new TextRun({ text: "", break: 1 }));
      }

      const textRun = new TextRun({
        text: item.text.trim(),
        rightToLeft: rtl,
      });
      paragraph.addChildElement(textRun);

      childrenArray.push(paragraph);
      item.metadata && childrenArray.push(new Paragraph({ text: "" }));
    });

    const doc = new Document({
      sections: [{ children: childrenArray }],
    });

    const buffer: any = await Packer.toBuffer(doc);

    if (isNativeApp()) {
      const base64 = Buffer.from(buffer).toString("base64");

      if (returnBlobOrBase64) return base64;

      await downloadFile({
        type: "base64",
        base64,
        fileName: `${fileName}.docx`,
      });
      return;
    }

    // Create a Blob with the content
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    if (returnBlobOrBase64) return blob;

    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);

    await downloadFile({
      type: "url",
      url,
      fileName: `${fileName}.docx`,
    });
};

// Imported files keep their extension in fileName ("call.mp3"), so exports
// replace it rather than stack another ("call.mp3.pdf"). Only known media and
// document extensions count: "Q1 v2.5 review" keeps its dot.
export const exportBaseName = (fileName: string): string => {
  const dot = fileName.lastIndexOf(".");
  return dot > 0 &&
    SUPPORTED_FILE_EXTENSIONS.includes(fileName.slice(dot).toLowerCase())
    ? fileName.slice(0, dot)
    : fileName;
};

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"]/g, (character) => `&#${character.charCodeAt(0)};`);

// Rendered by the server's Chromium (Noto core + CJK) because a client-side
// PDF can only draw the fonts it bundles: the old jsPDF export carried Arabic
// only, so Japanese, Hindi, Cyrillic, ... came out blank.
export const transcriptPdfHtml = ({
  items,
  direction,
  language,
}: {
  items: TranscriptExportItem[];
  direction: TextDirection;
  language?: string;
}): string => {
  const lang = language && !["auto", "unknown"].includes(language)
    ? ` lang="${escapeHtml(language)}"`
    : "";
  const paragraphs = items.map(({ metadata, text }) => {
    // Each "speaker • time" part is isolated so an RTL page keeps
    // "00:00 - 00:12" in reading order instead of mirroring it.
    const meta = metadata
      ? `<p style="margin:0;color:#666;font-size:10pt">${metadata
          .split(" • ")
          .map((part) => `<bdi>${escapeHtml(part)}</bdi>`)
          .join(" • ")}</p>`
      : "";
    return `${meta}<p style="margin-top:0">${escapeHtml(text.trim())}</p>`;
  });
  return `<div dir="${direction}"${lang}>${paragraphs.join("")}</div>`;
};

const renderTranscriptPdf = async (
  transcript: Transcript,
  items: TranscriptExportItem[],
  direction: TextDirection,
): Promise<string> => {
  const result = await actions.aiContent.markdownToPdf(
    transcript.id!,
    transcriptPdfHtml({ items, direction, language: transcript.language }),
  );
  if ("error" in result) throw createActionError(result.error);
  return result.value;
};

async function downloadTranscriptAsTxtOrSrt(params: {
  fileName?: string;
  items: { text: string; metadata?: string }[];
  returnBlobOrBase64?: boolean;
  exportType?: "txt" | "srt";
}): Promise<Blob | string | undefined> {
    const fileName =
      params.fileName || `transcript.${params.exportType || "txt"}`;
    let formattedContent = "";

    for (let i = 0; i < params.items.length; i++) {
      const { metadata, text } = params.items[i];

      // For SRT format, add sequence number
      if (params.exportType === "srt" && metadata) {
        formattedContent += i + 1 + "\n";
      }

      if (metadata) {
        formattedContent += metadata + "\n";
      }

      formattedContent += text.trim() + "\n\n";
    }

    if (isNativeApp()) {
      const base64 = Buffer.from(formattedContent).toString("base64");

      if (params.returnBlobOrBase64) {
        return base64;
      }

      await downloadFile({
        type: "base64",
        base64,
        fileName,
      });
      return;
    }

    // Create a Blob with the content
    const blob = new Blob([formattedContent], { type: "text/plain" });

    if (params.returnBlobOrBase64) {
      return blob;
    }

    await downloadFile({
      type: "url",
      url: URL.createObjectURL(blob),
      fileName,
    });
}

export type TranscriptExportType = "docx" | "pdf" | "txt" | "srt";

export type TranscriptExportOptions = {
  exportType: TranscriptExportType;
  withTimestamps: boolean;
  withSpeakers: boolean;
  withMonologue: boolean;
};

export type TranscriptExportItem = {
  metadata?: string;
  text: string;
};

const subtitleTime = (timeInMs: number): string => {
  const milliseconds = Math.floor(timeInMs % 1000);
  const seconds = Math.floor((timeInMs / 1000) % 60);
  const minutes = Math.floor((timeInMs / (1000 * 60)) % 60);
  const hours = Math.floor((timeInMs / (1000 * 60 * 60)) % 24);
  return (
    `${String(hours).padStart(2, "0")}:` +
    `${String(minutes).padStart(2, "0")}:` +
    `${String(seconds).padStart(2, "0")},` +
    `${String(milliseconds).padStart(3, "0")}`
  );
};

const speakerLabel = (speaker?: string): string =>
  !speaker
    ? i18n.t("transcript.unknownSpeaker")
    : isNaN(Number(speaker))
    ? speaker
    : `${i18n.t("actions.speaker")} ${Number(speaker) + 1}`;

export const buildTranscriptExport = (
  transcript: Transcript,
  options: TranscriptExportOptions
): TranscriptExportItem[] => {
  if (options.exportType === "srt") {
    return transcript.segments.map((segment) => ({
      metadata: `${subtitleTime(segment.start * 1000)} --> ${subtitleTime(
        segment.end * 1000
      )}`,
      text: options.withSpeakers
        ? `${speakerLabel(segment.speaker)}: ${segment.text}`
        : segment.text,
    }));
  }

  if (options.withMonologue) {
    return [{ text: transcript.segments.map((segment) => segment.text).join(" ") }];
  }

  return transcript.segments.map((segment) => {
    const speaker = options.withSpeakers
      ? speakerLabel(segment.speaker)
      : undefined;
    const timestamps = options.withTimestamps
      ? `${formatDuration(segment.start)} - ${formatDuration(segment.end)}`
      : undefined;
    return {
      metadata:
        speaker && timestamps
          ? `${speaker} • ${timestamps}`
          : speaker || timestamps,
      text: segment.text,
    };
  });
};

const encodeTranscript = async (
  transcript: Transcript,
  options: TranscriptExportOptions
): Promise<Blob | string | undefined> => {
  const items = buildTranscriptExport(transcript, options);
  const contentDirection = getTranscriptDirection(transcript);
  const direction =
    contentDirection === "auto"
      ? getTextDirection(items.map((item) => item.text).join(" "))
      : contentDirection;
  switch (options.exportType) {
    case "txt":
    case "srt":
      return downloadTranscriptAsTxtOrSrt({
        items,
        returnBlobOrBase64: true,
        exportType: options.exportType,
      });
    case "pdf":
      return renderTranscriptPdf(transcript, items, direction);
    case "docx":
      return downloadTranscriptAsDocx({
        items,
        direction,
        returnBlobOrBase64: true,
      });
  }
};

export const exportTranscript = async (
  transcript: Transcript,
  options: TranscriptExportOptions
): Promise<void> => {
  const items = buildTranscriptExport(transcript, options);
  const contentDirection = getTranscriptDirection(transcript);
  const direction =
    contentDirection === "auto"
      ? getTextDirection(items.map((item) => item.text).join(" "))
      : contentDirection;
  switch (options.exportType) {
    case "txt":
    case "srt":
      await downloadTranscriptAsTxtOrSrt({
        items,
        fileName: `${exportBaseName(transcript.fileName)}.${options.exportType}`,
        exportType: options.exportType,
      });
      return;
    case "docx":
      await downloadTranscriptAsDocx({
        fileName: exportBaseName(transcript.fileName),
        items,
        direction,
      });
      return;
    case "pdf": {
      const toastId = toast.loading(i18n.t("utils.downloadingFile"));
      try {
        await downloadFile({
          type: "url",
          url: `data:application/pdf;base64,${await renderTranscriptPdf(transcript, items, direction)}`,
          fileName: `${exportBaseName(transcript.fileName)}.pdf`,
        });
      } catch {
        toast.error(i18n.t("utils.downloadFailed"));
      } finally {
        toast.dismiss(toastId);
      }
    }
  }
};

export const exportTranscriptArchive = async (
  transcripts: Transcript[],
  options: TranscriptExportOptions,
  now: Date = new Date()
): Promise<{ succeeded: number; failed: number }> => {
  const folderName = `1Transcribe_Export_${now.toISOString().slice(0, 10)}`;
  const archive = new JSZip();
  const folder = archive.folder(folderName);
  const usedNames = new Set<string>();
  let succeeded = 0;
  let failed = 0;

  for (const transcript of transcripts) {
    const baseName = exportBaseName(transcript.fileName);
    let entryName = `${baseName}.${options.exportType}`;
    let duplicate = 2;
    while (usedNames.has(entryName)) {
      entryName = `${baseName} (${duplicate}).${options.exportType}`;
      duplicate += 1;
    }

    try {
      const output = await encodeTranscript(transcript, options);
      if (!output || !folder) throw new Error("Empty transcript export");
      usedNames.add(entryName);
      if (typeof output === "string") {
        folder.file(entryName, output, { base64: true });
      } else {
        folder.file(entryName, output);
      }
      succeeded += 1;
    } catch {
      failed += 1;
    }
  }

  if (succeeded > 0) {
    const blob = await archive.generateAsync({ type: "blob" });
    await downloadFile({
      type: "url",
      url: URL.createObjectURL(blob),
      fileName: `${folderName}.zip`,
    });
  }
  return { succeeded, failed };
};

export const transcriptPlainText = (transcript: Transcript): string =>
  transcript.segments.map((segment) => segment.text).join("\n\n");

export const downloadTranscriptAudio = async (
  transcript: Transcript
): Promise<void> => {
  const servedUrl = transcript.originalFileUrl || "";
  let pathname = "";
  try {
    pathname = new URL(servedUrl, "https://client.invalid").pathname;
  } catch {
    pathname = servedUrl.split(/[?#]/)[0];
  }
  const fileSegment = pathname.split("/").pop() || "";
  const extension = fileSegment.match(/\.[a-zA-Z0-9]{1,10}$/)?.[0] || "";
  await downloadFile({
    type: "url",
    url: servedUrl,
    fileName: `${exportBaseName(transcript.fileName)}${extension}`,
    isLargeFile: getPlatform() === "android",
  });
};
